"use client";

import { useContext, useEffect, useMemo } from "react";
import { TextField, Autocomplete } from "@mui/material";
import { FilterParamsContext } from "../FilterParamsProvider";
import { County, State } from "@/postgres";

export default function CountySelectClient({
  options,
  stateMap,
}: {
  options: County[];
  stateMap: Map<string, State>;
}) {
  const { filterParams, setFilterParams } = useContext(FilterParamsContext);
  const countyMap = useMemo(
    () =>
      new Map(
        [...options]
          .filter((county) => {
            if (filterParams.states?.length) {
              return filterParams.states
                .map((id) => `${id}`)
                .includes(county.state_id);
            }
            return true;
          })
          .sort(
            (countyA, countyB) =>
              parseInt(countyA.state_id) - parseInt(countyB.state_id),
          )
          .map((county) => [county.id, county]),
      ),
    [filterParams.states, options],
  );

  useEffect(() => {
    // Remove counties that are no longer valid based on selected states
    const validCountyIds = new Set(countyMap.keys());
    const validFilterIds = filterParams.counties?.filter((id) =>
      validCountyIds.has(`${id}`),
    );

    if (
      JSON.stringify(validFilterIds) !== JSON.stringify(filterParams.counties)
    ) {
      setFilterParams((prev) => ({
        ...prev,
        counties: validFilterIds,
      }));
    }
  }, [countyMap, filterParams.counties, filterParams.states, setFilterParams]);

  return (
    <Autocomplete
      multiple
      options={Array.from(countyMap.keys())}
      groupBy={(option) => {
        const stateId = countyMap.get(option)?.state_id;
        if (!stateId) {
          return "";
        }
        return stateMap.get(stateId)?.name || "";
      }}
      value={filterParams.counties?.map((id) => `${id}`)}
      onChange={(_event, newValue) => {
        setFilterParams({
          ...filterParams,
          counties: [...newValue]
            .sort((a, b) => {
              const countyA = countyMap.get(a);
              const countyB = countyMap.get(b);
              return (countyA?.name || "").localeCompare(countyB?.name || "");
            })
            .map((id) => parseInt(id)),
        });
      }}
      renderInput={(params) => <TextField {...params} label="County" />}
      getOptionLabel={(option) => countyMap.get(option)?.name || ""}
      getOptionKey={(option) => countyMap.get(option)?.id || Date.now()}
      disableCloseOnSelect
    />
  );
}
